// Keep this module's import graph lean (constants/routes + types only) so
// it stays unit-testable in the node jest environment without a DOM.
import queryString from 'query-string';
import {
  EXTERNAL_PURCHASE_SUCCESS_ROUTE,
  MARKET_SCAN_RESULTS_ROUTE,
} from 'constants/routes';
import { POLICY_SCAN_STATUS } from 'types';

// Firebase realtime payload pushed by the API's conversionNotification
// webhook when an external provider (e.g. Autosure) confirms a purchase
// made on their own site.
export interface IExternalPurchaseNotification {
  type?: string;
  status?: string;
  sk?: string; // the purchased policy id
  policyScanId?: string;
  policyType?: string;
  policyProviderName?: string;
}

/**
 * Returns the external-purchase success route to navigate to when a
 * PURCHASED realtime notification arrives while the user is parked on the
 * market scan results page of that same scan (the tab they left open when
 * the provider's site was opened in a new tab). Returns null otherwise so
 * the user is never yanked out of an unrelated page mid-task - in that
 * case the regular in-app notification still announces the purchase.
 */
export const getExternalPurchaseSuccessRoute = (
  data: IExternalPurchaseNotification | undefined,
  pathname: string,
  search: string,
): string | null => {
  if (
    !data ||
    data.status !== POLICY_SCAN_STATUS.PURCHASED ||
    !data.sk ||
    !data.policyScanId ||
    !data.policyProviderName
  ) {
    return null;
  }

  if (!pathname.startsWith(MARKET_SCAN_RESULTS_ROUTE)) {
    return null;
  }

  const { id } = queryString.parse(search.replace('?', ''));
  if (id !== data.policyScanId) {
    return null;
  }

  return `${EXTERNAL_PURCHASE_SUCCESS_ROUTE}?${queryString.stringify({
    policyId: data.sk,
    policyScanId: data.policyScanId,
    provider: data.policyProviderName,
  })}`;
};
